import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { animateScroll } from 'react-scroll';

import teamInfo from '../data/teamInfo';
import timelineInfo from '../data/timelineInfo';
import membersInfo from '../data/membersInfo';
import * as backgroundImages from '../data/backgroundImages';

const defaultColors = {
    '--text-hover': '#676D75',
    '--text-contrast': '#FFF',
    '--primary': '#33263A',
    '--secondary': '#d1cd1a',
};

const setThemeColor = colors => {
    const root = document.querySelector(':root');

    Object.keys(colors).map(key => root.style.setProperty(key, colors[key]));
};

const setTimelineInfo = (timeline, info) => {
    const newTimeline = timeline.map(item => {
        return {
            ...item,
            directors: item.directors.map(director => {
                if (info[director.index])
                    return {
                        ...director,
                        ...info[director.index],
                    };

                return director;
            }),
            members: item.members.map(member => {
                if (info[member.index])
                    return {
                        ...member,
                        ...info[member.index],
                    };

                return member;
            }),
        };
    });

    return newTimeline;
};

const DataContext = React.createContext();

const Data = ({ children }) => {
    const [team, setTeam] = useState(teamInfo);
    const [timeline, setTimeline] = useState(
        setTimelineInfo(timelineInfo, membersInfo),
    );
    const [images, setImages] = useState(backgroundImages);

    const setTheme = useCallback(newTheme => {
        // Dynamically imports theme assets, then set them
        switch (newTheme) {
            case 'egirl':
                import('../utils/setEgirlTheme').then(egirlTheme => {
                    setTeam(egirlTheme.setTeam(teamInfo));
                    setTimeline(timeline =>
                        setTimelineInfo(timeline, egirlTheme.getFemaleTeam()),
                    );
                    setThemeColor(egirlTheme.colors);
                    setImages(egirlTheme.femaleImages);
                });
                break;
            case 'machoman':
                import('../utils/setMachoTheme').then(machoTheme => {
                    setTeam(machoTheme.setTeam(teamInfo));
                    setTimeline(timeline =>
                        setTimelineInfo(timeline, machoTheme.getMachoTeam()),
                    );
                    setThemeColor(machoTheme.colors);
                    setImages(machoTheme.machoImages);
                });
                break;
            default:
                setImages(backgroundImages);
                setThemeColor(defaultColors);
        }
        animateScroll.scrollToTop();
    }, []);

    return (
        <DataContext.Provider value={{ team, timeline, images, setTheme }}>
            {children}
        </DataContext.Provider>
    );
};

Data.propTypes = {
    children: PropTypes.node,
};

export default Data;

export const useData = () => {
    const context = React.useContext(DataContext);

    return context;
};

export const useTeam = () => {
    const { team } = useData();

    return team;
};

export const useImage = () => {
    const { images } = useData();

    return images;
};
